import { Component, ElementRef, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

import * as d3 from 'd3';
import { GraphUtilsService } from '../../services/graph-utils.service';
import { DataService } from '../../services/data.service';
import { values } from 'd3';

@Component({
    selector: 'app-histogram',
    templateUrl: './histogram.component.html',
    styleUrls: ['./histogram.component.scss'],
    standalone: false
})
export class HistogramComponent implements OnChanges, OnDestroy {
  @Input() kpi: any;
  @Input() data: any[];
  @Input() outliers: any;
  @Input() uslLsl: boolean;

  thresholds: any = {
    usl: null,
    lsl: null,
    ucl: null,
    lcl: null,
    avg: null,
    avgPlus1Sigma: null,
    avgMinus1Sigma: null,
    avgPlus2Sigma: null,
    avgMinus2Sigma: null
  };

  svg: any = null;
  tip: any = null;

  subscription: any;
  constructor(
    private elRef: ElementRef,
    private graphUtil: GraphUtilsService,
    private dataSer: DataService
  ) {
    this.subscription = dataSer.events.subscribe((res: any) => {
      if (res.name === 'replotGraph') {
        let timeoutval = 10;
        if (res.args && res.args[0] === 'menuToggle') {
          timeoutval = 101;
        }

        setTimeout(() => {
          this.checkAndReplot();
        }, timeoutval);
      }
    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue && changes.data.currentValue.length) {
      if (!this.svg) {
        this.initGraph();
      }
      this.checkAndReplot();
    }
  }

  checkAndReplot() {


    if (this.svg) {
      this.svg.selectAll('*').remove();
    }
    if (this.tip) {
      d3.selectAll('.tooltip.d3-tip').remove();
    }
    if (this.svg && this.data && this.data.length && this.kpi.avg !== null) {
      this.thresholds = this.graphUtil.setThreshHolds(this.kpi, this.outliers, this.uslLsl);
      this.drawHistogram();
    }
  }

  calculateticks = (bounds) => {
    const a = this.data.map(elem => elem.value);
    const n = a.length;
    a.sort((a, b) => a - b);
    const Q1 = ((n + 1) / 4);
    const q1tem = a[Math.floor(Q1)];
    const Q3 = ((3 * (n + 1)) / 4)
    const q3term = a[Math.floor(Q3)];
    const IQR = q3term - q1tem;
    const range = a[a.length - 1] - a[0];
    const cubeRoot = Math.round(Math.cbrt(this.data.length));
    const binwidth = 2 * (IQR / cubeRoot);
    const maxBinwidth = (range / binwidth);
    const binsarea = range / maxBinwidth;
    const rangeAll = bounds.max - bounds.min;

    let ticks = rangeAll / binsarea;
    if (isNaN(ticks)) {
      ticks = 30;
    }
    ticks = (ticks * 100) / 120;
    return ticks;
  }

  initGraph() {
    this.svg = d3.select(this.elRef.nativeElement.children[0]).append('svg')
      .attr('class', 'histogram-svg')
      .attr('width', '100%')
      .attr('height', '100%');
  }

  drawHistogram = () => {
    const margin = { top: 60, right: 30, bottom: 30, left: 40 };
    const width = +this.svg.node().clientWidth - margin.left - margin.right;
    const height = +this.svg.node().clientHeight - margin.top - margin.bottom;

    const boundsAll = this.graphUtil.findBoundaryVals(this.data, 'value', [], 'histogram');
    const bounds = this.graphUtil.findBoundaryVals(this.data, 'value', this.thresholds, 'histogram');
    const x: any = d3.scaleLinear()
      .domain([bounds.min, bounds.max])
      .range([0, width]);

    const histogram = d3.histogram()
      .value((d: any) => {
        return d;
      })
      .domain(x.domain())
      .thresholds(x.ticks(this.calculateticks(bounds)));

    const bins = histogram(this.data.map(elem => elem.value));
    const maxYVal = d3.max(bins, (d: any) => {
      return d.length;
    }) * (1.03);

    const y: any = d3.scaleLinear()
      .range([height, 0])
      .domain([0, maxYVal]);

    const graph = this.svg.append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    this.setTip();

    graph.append('g')
      .attr('class', 'axis x')
      .attr('transform', 'translate(0,' + height + ')')
      .call(d3.axisBottom(x));

    graph.append('g')
      .attr('class', 'axis y')
      .call(d3.axisLeft(y));
    const barPlots = graph.append('g')
      .attr('class', 'bars')
      .selectAll('rect')
      .data(bins)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 1)
      .attr('transform', (d: any) => {
        return 'translate(' + x(d.x0) + ',' + y(d.length) + ')';
      })
      .attr('width', (d: any) => {
        return (x(d.x1) - x(d.x0) - 1) >= 0 ? (x(d.x1) - x(d.x0) - 1) : 0;
      })
      .attr('height', (d: any) => {
        return height - y(d.length);
      })
      .on('mouseover', this.onHover.bind(this))
      .on('mousemove', this.onMouseMove.bind(this))
      .on('mouseout', this.onMouseOut.bind(this));

    const threshholdLines = graph.append('g')
      .attr('class', 'threshholdLines');
    const labelSize = {
      height: 20,
      sidePadding: 10
    };

    // Gaussian curve - removed for now

    const lineDraw = d3.line()
      .curve(d3.curveMonotoneX)
      .x((d: any) => {
        return x(d.x0) + ((x(d.x1) - x(d.x0) - 1) >= 0 ? (x(d.x1) - x(d.x0) - 1) / 2 : 0) + 1;
      }).y((d: any) => y(d.length));

    // only if sd != 0
    if (this.kpi && this.kpi.sigmaval && this.kpi.withOutliersSigmaval) {
      graph.append('g')
        .attr('class', 'curve')
        .append('path')
        .datum(bins)
        .attr('class', 'curve')
        .attr('stroke', '#004976')
        .attr('fill', 'none')
        .attr('d', lineDraw);

      // const dots = graph.append('g') - removed dot due to duplication
      graph.append('g')
        .attr('class', 'dots')
        .attr('transform', 'translate(0,0)')
        .selectAll('.dot')
        .data(bins)
        .enter().append('circle')
        .attr('cx', (d, i) => {
          return x(d.x0) + ((x(d.x1) - x(d.x0) - 1) >= 0 ? (x(d.x1) - x(d.x0) - 1) / 2 : 0) + 1;
        })
        .attr('cy', (d) => {
          return y(d.length);
        })
        .attr('r', 1)
        .attr('fill', '#004976');
    } else {
      const sdValue = this.data.length ? this.data[0].value : null;
      threshholdLines.append('line')
        .attr('class', 'threshold-line sd')
        .attr('x1', x(sdValue))
        .attr('x2', x(sdValue))
        .attr('y1', y(0))
        .attr('y2', 0);
    }

    if (this.uslLsl) {
      if (this.thresholds.usl >= bounds.min && this.thresholds.usl <= bounds.max) {
        const text = (+this.thresholds.usl).toFixed(2);
        const calculatedWidth = (text.length * 10) + 2 * labelSize.sidePadding;

        threshholdLines.append('line')
          .attr('class', 'threshold-line usl')
          .attr('x1', x(this.thresholds.usl))
          .attr('x2', x(this.thresholds.usl))
          .attr('y1', y(0))
          .attr('y2', -10);

        const labelUSL = threshholdLines.append('g')
          .attr('class', 'label usl');
        labelUSL.append('rect')
          .attr('x', x(this.thresholds.usl))
          .attr('y', -25)
          .attr('width', calculatedWidth)
          .attr('height', labelSize.height)
          .attr('fill', '#6c7180');
        labelUSL.append('text')
          .attr('x', x(this.thresholds.usl) + calculatedWidth / 2)
          .attr('y', -12)
          .text('USL: ' + text)
          .attr('fill', '#fff')
          .style('font-size', '11px')
          .style('text-anchor', 'middle');
      }

      if (this.thresholds.lsl >= bounds.min && this.thresholds.lsl <= bounds.max) {
        const text = (+this.thresholds.lsl).toFixed(2);
        const calculatedWidth = (text.length * 10) + 2 * labelSize.sidePadding;

        threshholdLines.append('line')
          .attr('class', 'threshold-line lsl')
          .attr('x1', x(this.thresholds.lsl))
          .attr('x2', x(this.thresholds.lsl))
          .attr('y1', y(0))
          .attr('y2', -10);

        const labelLSL = threshholdLines.append('g')
          .attr('class', 'label lsl');
        labelLSL.append('rect')
          .attr('x', (x(this.thresholds.lsl) - calculatedWidth) > bounds.min ? x(this.thresholds.lsl) - calculatedWidth :
            x(this.thresholds.lsl))
          .attr('y', -25)
          .attr('width', calculatedWidth)
          .attr('height', labelSize.height)
          .attr('fill', '#6c7180');
        labelLSL.append('text')
          .attr('x', (x(this.thresholds.lsl) - calculatedWidth) > bounds.min ?
            x(this.thresholds.lsl) - calculatedWidth + calculatedWidth / 2 : x(this.thresholds.lsl) + calculatedWidth / 2)
          .attr('y', -12)
          .text('LSL: ' + text)
          .attr('fill', '#fff')
          .style('font-size', '11px')
          .style('text-anchor', 'middle');
      }
    }
    // Target
    if (this.thresholds.target >= bounds.min && this.thresholds.target <= bounds.max) {
      const text = (+this.thresholds.target).toFixed(2);
      const calculatedWidth = (text.length * 10) + 4 * labelSize.sidePadding;

      threshholdLines.append('line')
        .attr('class', 'threshold-line target')
        .attr('x1', x(this.thresholds.target))
        .attr('x2', x(this.thresholds.target))
        .attr('y1', y(0))
        .attr('y2', 15);

      const labelTARGET = threshholdLines.append('g')
        .attr('class', 'label lsl');
      labelTARGET.append('rect')
        .attr('x', x(this.thresholds.target))
        .attr('y', -5)
        .attr('width', calculatedWidth)
        .attr('height', labelSize.height)
        .attr('fill', '#fd7e14');
      labelTARGET.append('text')
        .attr('x', x(this.thresholds.target) + calculatedWidth / 2)
        .attr('y', 8)
        .text('TARGET: ' + text)
        .attr('fill', '#fff')
        .style('font-size', '11px')
        .style('text-anchor', 'middle');
    }
    if (this.thresholds.ucl >= bounds.min && this.thresholds.ucl <= bounds.max) {
      const text = (+this.thresholds.ucl).toFixed(2);
      const calculatedWidth = (text.length * 10) + 2 * labelSize.sidePadding;

      threshholdLines.append('line')
        .attr('class', 'threshold-line ucl')
        .attr('x1', x(this.thresholds.ucl))
        .attr('x2', x(this.thresholds.ucl))
        .attr('y1', y(0))
        .attr('y2', 55);

      const labelUCL = threshholdLines.append('g')
        .attr('class', 'label ucl');
      labelUCL.append('rect')
        .attr('x', x(this.thresholds.ucl))
        .attr('y', 35)
        .attr('width', calculatedWidth)
        .attr('height', labelSize.height)
        .attr('fill', '#ef5454');
      labelUCL.append('text')
        .attr('x', x(this.thresholds.ucl) + calculatedWidth / 2)
        .attr('y', 48)
        .text('UCL: ' + text)
        .attr('fill', '#fff')
        .style('font-size', '11px')
        .style('text-anchor', 'middle');
    }

    if (this.thresholds.lcl >= bounds.min && this.thresholds.lcl <= bounds.max) {
      const text = (+this.thresholds.lcl).toFixed(2);
      const calculatedWidth = (text.length * 10) + 2 * labelSize.sidePadding;
      threshholdLines.append('line')
        .attr('class', 'threshold-line lcl')
        .attr('x1', x(this.thresholds.lcl))
        .attr('x2', x(this.thresholds.lcl))
        .attr('y1', y(0))
        .attr('y2', 35);
      const labelLCL = threshholdLines.append('g')
        .attr('class', 'label lcl');
      labelLCL.append('rect')
        .attr('x', x(this.thresholds.lcl))
        .attr('y', 15)
        .attr('width', calculatedWidth)
        .attr('height', labelSize.height)
        .attr('fill', '#ef5454');
      labelLCL.append('text')
        .attr('x', (x(this.thresholds.lcl)) + calculatedWidth / 2)
        .attr('y', 28)
        .text('LCL: ' + text)
        .attr('fill', '#fff')
        .style('font-size', '11px')
        .style('text-anchor', 'middle');
    }

    if (this.thresholds.avg >= bounds.min && this.thresholds.avg <= bounds.max) {
      const text = (+this.thresholds.avg).toFixed(2);
      const calculatedWidth = (text.length * 10) + 2 * labelSize.sidePadding;

      threshholdLines.append('line')
        .attr('class', 'threshold-line avg')
        .attr('x1', x(this.thresholds.avg))
        .attr('x2', x(this.thresholds.avg))
        .attr('y1', y(0))
        .attr('y2', 70);

      const labelAVG = threshholdLines.append('g')
        .attr('class', 'label avg');
      labelAVG.append('rect')
        .attr('x', (x(this.thresholds.avg) - calculatedWidth) > bounds.min ? x(this.thresholds.avg) - calculatedWidth :
          x(this.thresholds.avg))
        .attr('y', 55)
        .attr('width', calculatedWidth)
        .attr('height', labelSize.height)
        .attr('fill', '#699a70');
      labelAVG.append('text')
        .attr('x', (x(this.thresholds.avg) - calculatedWidth) > bounds.min ?
          x(this.thresholds.avg) - calculatedWidth + calculatedWidth / 2 : x(this.thresholds.avg) + calculatedWidth / 2)
        .attr('y', 68)
        .text('AVG: ' + text)
        .attr('fill', '#fff')
        .style('font-size', '11px')
        .style('text-anchor', 'middle');
    }
  }

  setTip() {
    this.tip = d3.select('body').append('div')
      .attr('class', 'tooltip d3-tip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('z-index', '99999')
      .style('display', 'block');
  }

  onHover(d: any) {
    if (this.tip) {
      d3.selectAll('.tooltip.d3-tip').remove();
    }
    this.setTip();
    this.tip.style('opacity', .9);
    this.tip.html('<div><span>Range:&nbsp;</span>' +
      '<span>' + d.x0.toFixed(2) + '-' + d.x1.toFixed(2) + '</span> </div>' +
      '<div><span>Count:&nbsp;</span>' +
      '<span>' + d.length + '</span> </div>');
    return this.tip;
  }

  onMouseMove() {
    return this.tip.style('top',
      (d3.event.pageY - 60) + 'px').style('left', (d3.event.pageX - 140) + 'px');
  }

  onMouseOut(d: any) {
    return this.tip.style('display', 'none');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
